import { FileUploadBox, useFileUploadState } from "@9amhealth/shared";
import type {
  FileUploadConfig,
  FileUploadControls,
  FileUploadErrorType
} from "@9amhealth/shared";
import type { ReactNode } from "react";
import { useCallback, useEffect, useRef } from "react";
import FileType from "src/constants/fileType";
import {
  FILE_UPLOAD_MEGABYTE_LIMIT,
  UPLOAD_PAYLOAD_LIMIT_BINARY
} from "src/constants/limits";
import reportErrorSentry from "src/lib/reportErrorSentry";
import translate from "src/lib/translate";
import { fileState } from "src/state/state";
import Translate from "../Translate/Translate";

const MAX_SIZE_BYTES = UPLOAD_PAYLOAD_LIMIT_BINARY;
const MAX_SIZE_MB = FILE_UPLOAD_MEGABYTE_LIMIT;

interface FileUploadDocumentProps {
  onFileIdsChange?: (fileIds: string[]) => void;
  onReadyStateChange?: (ready: boolean) => void;
  /** File type stored as file attribute on upload. Defaults to LAB_RESULT. */
  fileType?: FileType;
  /** Tags stored as file attribute on upload. Defaults to ["lab-report"]. */
  fileTags?: string[];
  /** Title shown inside the upload box. */
  title?: ReactNode;
  /** Description/body shown inside the upload box. */
  description?: ReactNode;
  /** Label for the upload button. */
  uploadLabel?: ReactNode;
  /** Helper text shown below the upload button. */
  helperText?: ReactNode;
}

const FileUploadDocument = ({
  onFileIdsChange,
  onReadyStateChange,
  fileType = FileType.LAB_RESULT,
  fileTags = ["lab-report"],
  title = <Translate msg="initialLabs.noRecentLabsFound" />,
  description = <Translate msg="initialLabs.uploadDescription" />,
  uploadLabel = <Translate msg="fileUpload.uploadFile" />,
  helperText = <Translate msg="fileUpload.pdfOnlyHelper" />
}: FileUploadDocumentProps) => {
  const uploadedFileIdsRef = useRef<Map<File, string>>(new Map());

  const formatErrorMessage: FileUploadConfig["formatErrorMessage"] =
    useCallback(
      (error: { type: FileUploadErrorType; file: File }) =>
        error.type === "size"
          ? translate("error.fileSize", { maxSize: MAX_SIZE_MB })
          : translate("defaultError.fileTypeInvalid", { context: "PDF" }),
      []
    );

  const handleFileAccepted = useCallback(
    (file: File, { setStatus, setErrorMessage }: FileUploadControls) => {
      setStatus("uploading");

      fileState
        .uploadFile({
          file,
          fileAttributes: {
            "file.type": fileType,
            "file.tags": fileTags
          }
        })
        .then((result) => {
          if (!result) {
            throw new Error("Upload failed");
          }

          uploadedFileIdsRef.current.set(file, result.id);
          onFileIdsChange?.(Array.from(uploadedFileIdsRef.current.values()));
          setStatus("uploaded");
        })
        .catch((error) => {
          reportErrorSentry(error);
          setStatus("error");
          setErrorMessage(translate("fileUploadFailed"));
        });
    },
    [fileType, fileTags, onFileIdsChange]
  );

  const { instanceId, files, fileStatuses, fileErrors } = useFileUploadState({
    accept: "application/pdf,.pdf",
    maxSizeBytes: MAX_SIZE_BYTES,
    multiple: true,
    formatErrorMessage,
    onFileAccepted: handleFileAccepted
  });

  useEffect(() => {
    let changed = false;
    for (const [file] of uploadedFileIdsRef.current) {
      if (!files.includes(file)) {
        uploadedFileIdsRef.current.delete(file);
        changed = true;
      }
    }
    if (changed) {
      onFileIdsChange?.(Array.from(uploadedFileIdsRef.current.values()));
    }
  }, [files, onFileIdsChange]);

  useEffect(() => {
    const hasUploading = fileStatuses
      ? Array.from(fileStatuses.values()).includes("uploading")
      : false;
    const hasErrors = fileErrors ? fileErrors.size > 0 : false;
    onReadyStateChange?.(!hasUploading && !hasErrors);
  }, [fileStatuses, fileErrors, onReadyStateChange]);

  return (
    <FileUploadBox
      instanceId={instanceId}
      title={title}
      uploadLabel={uploadLabel}
      helperText={helperText}
    >
      {description}
    </FileUploadBox>
  );
};

export default FileUploadDocument;
